import { RestResponse } from '../../api/Rest';
import {
  ActivateSpaceMembershipTierInventoryBody,
  changeUserSpaceMembership,
  joinTeamForLeaderboard,
  LeaderboardData,
  MembershipData,
  searchSpaces as restSearchSpaces,
  SpacePerformanceReportData,
  updateAlbum as restUpdateAlbum,
} from '../../api/Rest/space';
import { ProfileData } from '../../api/utils';
import { UserId, Uuid } from '../Base';
import { Game, GameJson, jsonToGame } from '../GameStore';
import Repository, { Cursor, RepositoryJson } from '../Repository';
import { AsyncActionOptions, FetchActionResult } from '../Store';
import { WebBoosterType } from '../UserStore';
import { SystemInvitationType } from '../UserStore/Invitation';
import { FirstParameter, NonEmptyArray, SecondParameter } from '../Utils';

import { Album, AlbumJson } from './Album';
import { Leaderboard, LeaderboardJson } from './Leaderboard';
import { MultiplayerGame, MultiplayerGameJson } from './MultiplayerGame';
import {
  MultiplayerGameSession,
  MultiplayerGameSessionJson,
} from './MultiplayerGameSession';
import { PerformanceGame, PerformanceGameJson } from './PerformanceGame';
import { Space, SpaceJson } from './Space';
import { SpaceMembership, SpaceMembershipJson } from './SpaceMembership';
import {
  SpaceMembershipTier,
  SpaceMembershipTierJson,
} from './SpaceMembershipTier';
import {
  SpaceMembershipTierInventory,
  SpaceMembershipTierInventoryJson,
} from './SpaceMembershipTierInventory';
import {
  SpaceMembershipTierSubscription,
  SpaceMembershipTierSubscriptionJson,
} from './SpaceMembershipTierSubscription';
import { SpaceTeam, SpaceTeamJson } from './SpaceTeam';

// SpaceRepository
export enum SpaceListType {
  Recent = 'recent',
  Hot = 'hot', // hot classroomId
  Favourite = 'favourite',
  Liked = 'liked',
  Managing = 'managing',
  Following = 'following',
  Joined = 'joined',
  Recommended = 'recommended', // recommended classroomId
  SearchResult = 'searchResult', // search result classroomId
  RedeemShop = 'RedeemShop',
  'Album' = 'album',
  Home = 'home',
}

export enum SpaceMapType {
  'Album' = 'album', // classroom for album fetched not by classroom
  SearchHistory = 'SearchHistory', //activityId -> space
  NotificationSpace = 'notificationSpace', ///notification activityId? -> space
}

export type SpaceMapField = SpaceMapType;
export type SpaceListField = string | SpaceListType;
export class SpaceRepository extends Repository<
  Space,
  SpaceJson<Date | number>,
  SpaceMapField,
  SpaceListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceJson<Date | number>,
      SpaceMapField,
      SpaceListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [SystemInvitationType.JoinClub]: {},
          [SystemInvitationType.ReservedName]: {},
          [SystemInvitationType.OwnerShipTransfer]: {},
          [SpaceListType.Recent]: {},
          [SpaceListType.Hot]: {},
          [SpaceListType.Home]: {},
        }),
        mapFields: Repository.initFields(repository?.mapFields, {
          [SpaceMapType.Album]: {},
          [SpaceMapType.SearchHistory]: {},
          [SpaceMapType.NotificationSpace]: {},
        }),
      },
      (x) => new Space(x),
    );
  }
}

export enum SpaceMembershipMapType {
  Recent = 'Recent', // `${membership.classroomId}_${membership.userId}` => membership
  ActiveUser = 'activeUser', // classroomId -> active user's membership
}

export type SpaceMembershipMapField = string;
export type SpaceMembershipListField = string;
export class SpaceMembershipRepository extends Repository<
  SpaceMembership,
  SpaceMembershipJson<Date | number>,
  SpaceMembershipMapField,
  SpaceMembershipListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceMembershipJson<Date | number>,
      SpaceMembershipMapField,
      SpaceMembershipListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [SpaceMembershipMapType.Recent]: {},
          [SpaceMembershipMapType.ActiveUser]: {},
        }),
      },
      (x) => new SpaceMembership(x),
    );
  }
}

export enum SpaceMembershipTierMapType {
  ActiveUser = 'activeUser',
}
export type SpaceMembershipTierMapField = SpaceMembershipTierMapType;
export enum SpaceMembershipTierListType {
  All = 'all',
}
export type SpaceMembershipTierListField = string; // classroomId

export class SpaceMembershipTierRepository extends Repository<
  SpaceMembershipTier,
  SpaceMembershipTierJson<Date | number>,
  SpaceMembershipTierMapField,
  SpaceMembershipTierListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceMembershipTierJson<Date | number>,
      SpaceMembershipTierMapField,
      SpaceMembershipTierListField
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [SpaceMembershipTierMapType.ActiveUser]: {},
        }),
      },
      (x) => new SpaceMembershipTier(x),
    );
  }
}

export enum SpaceMembershipTierInventoryMapType {
  ActiveUser = 'activeUser',
}
export type SpaceMembershipTierInventoryMapField =
  SpaceMembershipTierInventoryMapType;
export enum SpaceMembershipTierInventoryListType {
  All = 'all',
}
export type SpaceMembershipTierInventoryListField = string; // classroomId

export class SpaceMembershipTierInventoryRepository extends Repository<
  SpaceMembershipTierInventory,
  SpaceMembershipTierInventoryJson<Date | number>,
  SpaceMembershipTierInventoryMapField,
  SpaceMembershipTierInventoryListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceMembershipTierInventoryJson<Date | number>,
      SpaceMembershipTierInventoryMapField,
      SpaceMembershipTierInventoryListField
    >,
  ) {
    super(
      {
        ...repository,
        mapFields: Repository.initFields(repository?.mapFields, {
          [SpaceMembershipTierInventoryMapType.ActiveUser]: {},
        }),
      },
      (x) => new SpaceMembershipTierInventory(x),
    );
  }
}

export type SpaceMembershipTierSubscriptionMapField = string;
export enum SpaceMembershipTierSubscriptionListType {
  ActiveUser = 'activeUser',
}
export type SpaceMembershipTierSubscriptionListField = string;

export class SpaceMembershipTierSubscriptionRepository extends Repository<
  SpaceMembershipTierSubscription,
  SpaceMembershipTierSubscriptionJson<Date | number>,
  SpaceMembershipTierSubscriptionMapField,
  SpaceMembershipTierSubscriptionListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceMembershipTierSubscriptionJson<Date | number>,
      SpaceMembershipTierSubscriptionMapField,
      SpaceMembershipTierSubscriptionListField
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [SpaceMembershipTierSubscriptionListType.ActiveUser]: {},
        }),
      },
      (x) => new SpaceMembershipTierSubscription(x),
    );
  }
}

// leaderboard
export enum SpaceLeaderboardListType {
  Recent = 'Recent',
}
export type SpaceLeaderboardMapField = string;
export type SpaceLeaderboardListField = string;
export class SpaceLeaderboardRepository extends Repository<
  Leaderboard,
  LeaderboardJson<Date | number>,
  SpaceLeaderboardMapField,
  SpaceLeaderboardListField
> {
  constructor(
    repository?: RepositoryJson<
      LeaderboardJson<Date | number>,
      SpaceLeaderboardMapField,
      SpaceLeaderboardListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [SpaceLeaderboardListType.Recent]: {},
        }),
      },
      (x) => new Leaderboard(x),
    );
  }
}

// team
export enum SpaceTeamListType {
  Recent = 'Recent',
}
export type SpaceTeamMapField = string;
export type SpaceTeamListField = string;
export class SpaceTeamRepository extends Repository<
  SpaceTeam,
  SpaceTeamJson<Date | number>,
  SpaceTeamMapField,
  SpaceTeamListField
> {
  constructor(
    repository?: RepositoryJson<
      SpaceTeamJson<Date | number>,
      SpaceTeamMapField,
      SpaceTeamListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [SpaceTeamListType.Recent]: {},
        }),
      },
      (x) => new SpaceTeam(x),
    );
  }
}

// multiplayerGame
export enum MultiplayerGameListType {
  Recent = 'Recent',
}
export type MultiplayerGameMapField = string;
export type MultiplayerGameListField = string;
export class MultiplayerGameRepository extends Repository<
  MultiplayerGame,
  MultiplayerGameJson<Date | number>,
  MultiplayerGameMapField,
  MultiplayerGameListField
> {
  constructor(
    repository?: RepositoryJson<
      MultiplayerGameJson<Date | number>,
      MultiplayerGameMapField,
      MultiplayerGameListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [MultiplayerGameListType.Recent]: {},
        }),
      },
      (x) => new MultiplayerGame(x),
    );
  }
}

// multiplayerGameSession
export enum MultiplayerGameSessionListType {
  Recent = 'Recent',
}
export type MultiplayerGameSessionMapField = string;
export type MultiplayerGameSessionListField = string;
export class MultiplayerGameSessionRepository extends Repository<
  MultiplayerGameSession,
  MultiplayerGameSessionJson<Date | number>,
  MultiplayerGameSessionMapField,
  MultiplayerGameSessionListField
> {
  constructor(
    repository?: RepositoryJson<
      MultiplayerGameSessionJson<Date | number>,
      MultiplayerGameSessionMapField,
      MultiplayerGameSessionListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [MultiplayerGameSessionListType.Recent]: {},
        }),
      },
      (x) => new MultiplayerGameSession(x),
    );
  }
}

// game
export enum GameListType {
  Recent = 'Recent',
}
export type GameMapField = string;
export type GameListField = string;
export class GameRepository extends Repository<
  Game,
  GameJson<number>,
  GameMapField,
  GameListField
> {
  constructor(
    repository?: RepositoryJson<
      GameJson<number>,
      GameMapField,
      GameListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [MultiplayerGameListType.Recent]: {},
        }),
      },
      (x) => jsonToGame(x),
    );
  }
}

export interface SpaceStoreStateJson {
  spaces: RepositoryJson<SpaceJson, SpaceMapField, SpaceListField, number>;
  albums: RepositoryJson<AlbumJson, AlbumMapField, AlbumListField, number>;
  memberships: RepositoryJson<
    SpaceMembershipJson,
    SpaceMembershipMapField,
    SpaceMembershipListField,
    number
  >;
  teams: RepositoryJson<
    SpaceTeamJson,
    SpaceTeamMapField,
    SpaceTeamListField,
    number
  >;
  leaderboards: RepositoryJson<
    LeaderboardJson,
    SpaceLeaderboardMapField,
    SpaceLeaderboardListField,
    number
  >;
  multiplayerGames: RepositoryJson<
    MultiplayerGameJson,
    MultiplayerGameMapField,
    MultiplayerGameListField,
    number
  >;
  multiplayerGameSessions: RepositoryJson<
    MultiplayerGameSessionJson,
    MultiplayerGameMapField,
    MultiplayerGameListField,
    number
  >;
  games: RepositoryJson<GameJson, GameMapField, GameListField, number>;
}

// AlbumRepository
export enum AlbumListType {
  Recent = 'recent',
  TryAlbum = 'tryAlbum',
  HomeAlbum = 'homeAlbum',
  Normal = 'normal',
}

export type AlbumMapField = string;
export type AlbumListField = string | AlbumListType;
export class AlbumRepository extends Repository<
  Album,
  AlbumJson<Date | number>,
  AlbumMapField,
  AlbumListField
> {
  constructor(
    repository?: RepositoryJson<
      AlbumJson<Date | number>,
      AlbumMapField,
      AlbumListField,
      Date | number
    >,
  ) {
    super(
      {
        ...repository,
        listFields: Repository.initFields(repository?.listFields, {
          [AlbumListType.Recent]: {},
          [AlbumListType.Normal]: {},
          [AlbumListType.HomeAlbum]: {},
        }),
      },
      (x) => new Album(x),
    );
  }
}

export type PerformanceGameMapField = string;
export type PerformanceGameListField = string;
export class PerformanceGameRepository extends Repository<
  PerformanceGame,
  PerformanceGameJson<Date | number>,
  PerformanceGameMapField,
  PerformanceGameListField
> {
  constructor(
    repository?: RepositoryJson<
      PerformanceGameJson<Date | number>,
      PerformanceGameMapField,
      PerformanceGameListField,
      Date | number
    >,
  ) {
    super({ ...repository }, (x) => new PerformanceGame(x));
  }
}

export interface SpaceStoreState {
  spaces: SpaceRepository;
  spaceMembershipTierInventories: SpaceMembershipTierInventoryRepository;
  spaceMembershipTierSubscriptions: SpaceMembershipTierSubscriptionRepository;
  spaceMembershipTiers: SpaceMembershipTierRepository;
  albums: AlbumRepository;
  spaceMemberships: SpaceMembershipRepository;
  teams: SpaceTeamRepository;
  leaderboards: SpaceLeaderboardRepository;
  games: GameRepository;
  performanceGames: PerformanceGameRepository;

  multiplayerGames: MultiplayerGameRepository;
  multiplayerGameSessions: MultiplayerGameSessionRepository;
}

export interface SpaceStoreAction {
  // space
  getSpace: (classroomId: Uuid) => Space | null | undefined;
  getSpaces: (fieldName: SpaceListField) => Space[];
  resetSpacesList: (fieldName: SpaceListField) => void;
  handleFetchSpacesResult: (
    fieldNames: string | string[],
    oldCursor: Cursor,
    classrooms: {
      group: SpaceJson;
      membership: SpaceMembershipJson | null;
      rootId: string;
    }[],
    profiles: ProfileData[],
    newCursor: Cursor,
  ) => FetchActionResult;

  fetchManagingSpaces: (
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;

  fetchManagingSpacesByUserId: (
    userId: Uuid,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;

  fetchSpace: (
    fieldNames: SpaceListField | NonEmptyArray<SpaceListField>,
    classroomId: Uuid,
  ) => Promise<void>;

  searchSpaces: (
    params: FirstParameter<typeof restSearchSpaces>,
    options?: AsyncActionOptions,
    isLog?: boolean,
  ) => Promise<FetchActionResult>;
  fetchHotSpaces: (
    params?: FirstParameter<typeof restSearchSpaces>,
    options?: AsyncActionOptions,
    isLog?: boolean,
  ) => Promise<FetchActionResult>;
  fetchRecentSpaces: (
    webBoosterType: WebBoosterType | undefined,
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;

  // inventory
  getTierInventories: (
    fieldName: SpaceMembershipTierInventoryListField,
  ) => SpaceMembershipTierInventory[];
  fetchTierInventories: (
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  activateTierInventory: (
    classroomId: Uuid,
    tierId: Uuid,
    params: ActivateSpaceMembershipTierInventoryBody,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;

  getTierSubscriptions: (
    fieldName: SpaceMembershipTierSubscriptionListField,
  ) => SpaceMembershipTierSubscription[];
  fetchTierSubscriptions: (
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;

  // tier
  getSpaceMemebershipTier: (
    classroomId: Uuid,
    tierId: Uuid,
  ) => SpaceMembershipTier | undefined | null;

  addSpaceToField: (fieldName: string, classroom: SpaceJson) => void;

  // prune
  onTierInventoryPrune: (inventory: SpaceMembershipTierInventory) => void;
  // album
  fetchAlbums: (
    params: {
      type: AlbumListType | string;
      classroomId: string;
      // state?: string | string[];
      partitionId?: string;
      // createdBy?: UserId;
      // startedBefore?: string;
      // endedAfter?: string;
      // leaderboardId?: string;
      // weeklyLeaderboard?: string;
      webBoosterType?: WebBoosterType | 'both';
      withAlbumSummaries?: boolean;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchAlbumsByIds: (
    params: {
      fieldName?: AlbumListField;
      classroomId: string;
      gameSetIds: string[];
      scopes?: string[];
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  fetchRecentAlbums: (
    params: {
      fieldName?: AlbumListType | string;
      classroomId?: string;
      withAlbumSummaries?: boolean;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  fetchAlbum: (
    params: {
      fieldName?: AlbumListField;
      classroomId: string;
      gameSetId: string;
      scope?: string;
    },
    options?: AsyncActionOptions,
  ) => Promise<void>;
  getAlbum: (gameSetId: Uuid) => Album | null | undefined;
  getAlbums: (fieldName: AlbumListField) => Album[];
  addAlbumToField: (fieldName: string, album: AlbumJson) => void;
  // fetchAlbum: (
  //   fieldNames: AlbumListField | NonEmptyArray<AlbumListField>,
  //   classroomId: Uuid,
  //   gameSetId: Uuid,
  //   scope?: string,
  // ) => Promise<void>;
  updateAlbum: (
    classroomId: Uuid,
    gameSetId: Uuid,
    params: SecondParameter<typeof restUpdateAlbum>,
    options?: AsyncActionOptions,
  ) => Promise<void>;
  deleteAlbum: (
    classroomId: Uuid,
    gameSetId: Uuid,
    options?: AsyncActionOptions,
  ) => Promise<boolean>;

  addMembershipToField: (
    fieldName: string,
    membership: SpaceMembershipJson,
  ) => void;
  getMembership: (
    classroomId: Uuid,
    userId: UserId,
  ) => SpaceMembership | null | undefined;

  getSpaceMembership: (
    classroomId: Uuid,
    userId: Uuid,
  ) => SpaceMembership | null | undefined;
  getActiveUserMembership: (
    classroomId: Uuid,
  ) => SpaceMembership | null | undefined;

  changeUserSpaceMembership: (
    classroomId: Uuid,
    body: SecondParameter<typeof changeUserSpaceMembership>,
  ) => Promise<RestResponse<MembershipData>>;
  joinTeamForLeaderboard: (
    classroomId: Uuid,
    leaderboardId: Uuid,
    body: SecondParameter<typeof joinTeamForLeaderboard>,
  ) => Promise<RestResponse<LeaderboardData>>;
  seeSpace: (classroomId: Uuid) => Promise<SpaceMembership | null | undefined>;
  getTeam: (teamId: Uuid) => SpaceTeam | null | undefined;
  addTeamToField: (fieldName: string, team: SpaceTeamJson) => void;
  getLeaderboard: (leaderboardId: Uuid) => Leaderboard | null | undefined;
  addLeaderboardToField: (
    fieldName: string,
    leaderboard: LeaderboardJson,
  ) => void;

  getMultiplayerGame: (
    multiplayerGameId: Uuid,
  ) => MultiplayerGame | null | undefined;
  addMultiplayerGameToField: (
    fieldName: string,
    multiplayerGame: MultiplayerGameJson,
  ) => void;

  getGame: (gameId: Uuid) => Game | null | undefined;
  addGameToField: (fieldName: string, game: GameJson) => void;

  getMultiplayerGameSession: (
    multiplayerGameSessionId: Uuid,
  ) => MultiplayerGameSession | null | undefined;
  addMultiplayerGameSessionToField: (
    fieldName: string,
    multiplayerGameSession: MultiplayerGameSessionJson,
  ) => void;

  getPerformanceReport: (
    params: {
      classroomId: string;
      webBoosterType?: WebBoosterType | 'both';
      partitionId?: string;
      from?: string;
      to?: string;
    },
    options?: AsyncActionOptions,
  ) => Promise<SpacePerformanceReportData | undefined>;
  fetchPerformanceReportGames: (
    params: {
      classroomId: string;
      fieldName: string;
      webBoosterType?: WebBoosterType | 'both';
      partitionId?: string;
      from?: string;
      to?: string;
    },
    cursor: Cursor,
    options?: AsyncActionOptions,
  ) => Promise<FetchActionResult>;
  addPerformanceGamesToField: (
    fieldName: string,
    performanceGames: PerformanceGameJson[],
  ) => void;
  getPerformanceReportGames: (fieldName: string) => PerformanceGame[];

  onAlbumPrune: (album: Album) => void;
}
